import { useRef, useState } from 'react';
import { useLoaderData, useRevalidator } from 'react-router';
import type { LumiCode } from '@auto-oms/shared/types/lumiCode';
import type { AddedProductCode } from '@auto-oms/shared/types/addedProductCode';
import { api, type ApiError } from '../lib/api';
import { pushToast } from '../lib/toast';
import {
  AddedProductCodeSection,
  loadAddedProductCodes,
} from './AddedProductCodePage';

interface LoaderData {
  items: LumiCode[];
  addedItems: AddedProductCode[];
}

export async function lumiCodeLoader(): Promise<LoaderData> {
  const [lumi, addedItems] = await Promise.all([
    api.get<{ items: LumiCode[] }>('/lumi-code'),
    loadAddedProductCodes(),
  ]);
  return { items: lumi.items, addedItems };
}

type EditCell = { id: number; field: keyof LumiCode };

const COLUMNS: { key: keyof LumiCode; label: string; editable: boolean }[] = [
  { key: 'channel', label: '채널코드', editable: true },
  { key: 'channelName', label: '채널명', editable: true },
  { key: 'pCodeCh', label: '채널 상품코드', editable: false },
  { key: 'pCode', label: '상품코드', editable: true },
  { key: 'oriCode', label: '품번코드', editable: true },
  { key: 'pName', label: '상품명', editable: true },
  { key: 'pDeliveryName', label: '발주 상품명', editable: true },
  { key: 'deliveryType', label: '배송방식', editable: true },
  { key: 'ea', label: '수량', editable: true },
  { key: 'deliveryPrice', label: '배송비', editable: true },
  { key: 'memo', label: '관리메모', editable: true },
];

const NUMERIC_FIELDS: ReadonlySet<keyof LumiCode> = new Set(['ea', 'deliveryPrice']);
const NARROW_FIELDS: ReadonlySet<keyof LumiCode> = new Set(['ea', 'deliveryPrice']);

export function LumiCodePage() {
  const { items: rawItems, addedItems } = useLoaderData() as LoaderData;
  const items = [...rawItems].sort((a, b) =>
    (a.channel ?? '').localeCompare(b.channel ?? '', 'ko'),
  );
  const { revalidate } = useRevalidator();
  const [editing, setEditing] = useState<EditCell | null>(null);
  const [draft, setDraft] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newRow, setNewRow] = useState({
    channel: 'LM',
    channelName: '',
    pCodeCh: '',
    pName: '',
    pDeliveryName: '',
    deliveryType: '무료',
    deliveryPrice: 0,
    ea: 1,
  });

  async function submitAdd() {
    if (adding) return;
    if (!newRow.pCodeCh.trim() || !newRow.pName.trim()) {
      pushToast('error', '채널 상품코드와 상품명은 필수입니다.');
      return;
    }
    setAdding(true);
    try {
      await api.post('/lumi-code', {
        channel: newRow.channel,
        channelName: newRow.channelName.trim() || null,
        pCodeCh: newRow.pCodeCh.trim(),
        pName: newRow.pName.trim(),
        pDeliveryName: newRow.pDeliveryName.trim() || newRow.pName.trim(),
        deliveryType: newRow.deliveryType.trim() || '무료',
        deliveryPrice: Number(newRow.deliveryPrice) || 0,
        ea: Math.max(1, Number(newRow.ea) || 1),
      });
      pushToast('success', `${newRow.channelName || newRow.channel} 상품 ${newRow.pCodeCh}을 추가했습니다.`);
      setNewRow({
        channel: 'LM',
        channelName: '',
        pCodeCh: '',
        pName: '',
        pDeliveryName: '',
        deliveryType: '무료',
        deliveryPrice: 0,
        ea: 1,
      });
      setAddOpen(false);
      revalidate();
    } catch (e) {
      const err = e as ApiError;
      pushToast('error', `추가 실패: ${err.message ?? '알 수 없는 오류'}`);
    } finally {
      setAdding(false);
    }
  }

  function startEdit(item: LumiCode, field: keyof LumiCode) {
    const col = COLUMNS.find((c) => c.key === field);
    if (!col?.editable) return;
    setEditing({ id: item.id, field });
    setDraft(item[field] != null ? String(item[field]) : '');
    setTimeout(() => inputRef.current?.select(), 0);
  }

  async function commitEdit() {
    if (!editing || saving) return;
    const original = items.find((i) => i.id === editing.id);
    const originalVal = original ? (original[editing.field] != null ? String(original[editing.field]) : '') : '';
    if (draft === originalVal) {
      setEditing(null);
      return;
    }
    setSaving(true);
    try {
      let value: string | number | null;
      if (draft === '') {
        value = null;
      } else if (NUMERIC_FIELDS.has(editing.field)) {
        const n = Number(draft);
        if (!Number.isFinite(n) || n < 0) {
          pushToast('error', '0 이상의 숫자만 입력할 수 있습니다.');
          setSaving(false);
          setEditing(null);
          return;
        }
        value = n;
      } else {
        value = draft;
      }
      await api.patch(`/lumi-code/${editing.id}`, { [editing.field]: value });
      pushToast('success', '저장했습니다.');
      revalidate();
    } catch (e) {
      const err = e as ApiError;
      pushToast('error', `저장 실패: ${err.message ?? '알 수 없는 오류'}`);
    } finally {
      setSaving(false);
      setEditing(null);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter') commitEdit();
    if (e.key === 'Escape') setEditing(null);
  }

  return (
    <div>
      <header className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="label-overline">매핑</p>
          <h1 className="mt-1 text-[28px] font-light tracking-[-0.01em] text-ink-950">상품 매칭 코드</h1>
          <p className="mt-1 text-[13px] text-ink-500">
            채널 상품코드와 발주 정보를 관리합니다.
            <span className="hidden sm:inline"> 셀을 클릭해 수정하세요.</span>
            <span className="sm:hidden"> 셀을 탭해 수정하세요. (가로 스크롤)</span>
          </p>
        </div>
        <button type="button" className="btn-primary self-start sm:self-auto" onClick={() => setAddOpen(true)}>
          행 추가
        </button>
      </header>

      {addOpen && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4"
          onClick={() => !adding && setAddOpen(false)}
        >
          <div
            className="flex max-h-[90vh] w-full max-w-[480px] flex-col overflow-y-auto rounded-t-lg bg-paper p-4 shadow-xl sm:rounded-lg sm:p-5"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <h3 className="text-base font-medium text-ink-900">상품 매칭 코드 추가</h3>
            <p className="mt-1 text-xs text-ink-500">수동주문 폼에서 선택할 채널 상품을 등록합니다.</p>
            <div className="mt-4 space-y-3">
              <div>
                <label className="block text-sm font-medium text-ink-700">채널 *</label>
                <select
                  value={newRow.channel}
                  onChange={(e) => setNewRow({ ...newRow, channel: e.target.value })}
                  className="mt-1 w-full rounded border border-ink-200 px-3 py-2.5 text-base focus:outline-none focus:ring-1 focus:ring-coral-500 sm:py-1.5 sm:text-sm"
                >
                  <option value="LM">LM</option>
                  <option value="COUPANG_R">COUPANG_R</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-ink-700">채널 표시명</label>
                <input
                  type="text"
                  value={newRow.channelName}
                  onChange={(e) => setNewRow({ ...newRow, channelName: e.target.value })}
                  placeholder="예: 루미네, 쿠팡로켓그로스"
                  maxLength={50}
                  className="mt-1 w-full rounded border border-ink-200 px-3 py-2.5 text-base focus:outline-none focus:ring-1 focus:ring-coral-500 sm:py-1.5 sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-ink-700">채널 상품코드 *</label>
                <input
                  type="text"
                  value={newRow.pCodeCh}
                  onChange={(e) => setNewRow({ ...newRow, pCodeCh: e.target.value })}
                  maxLength={20}
                  className="mt-1 w-full rounded border border-ink-200 px-3 py-2.5 text-base focus:outline-none focus:ring-1 focus:ring-coral-500 sm:py-1.5 sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-ink-700">상품명 *</label>
                <input
                  type="text"
                  value={newRow.pName}
                  onChange={(e) => setNewRow({ ...newRow, pName: e.target.value })}
                  maxLength={100}
                  className="mt-1 w-full rounded border border-ink-200 px-3 py-2.5 text-base focus:outline-none focus:ring-1 focus:ring-coral-500 sm:py-1.5 sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-ink-700">발주 상품명</label>
                <input
                  type="text"
                  value={newRow.pDeliveryName}
                  onChange={(e) => setNewRow({ ...newRow, pDeliveryName: e.target.value })}
                  placeholder="비워두면 상품명과 동일"
                  maxLength={100}
                  className="mt-1 w-full rounded border border-ink-200 px-3 py-2.5 text-base focus:outline-none focus:ring-1 focus:ring-coral-500 sm:py-1.5 sm:text-sm"
                />
              </div>
              <div className="flex flex-wrap gap-3">
                <div className="min-w-[180px] flex-1">
                  <label className="block text-sm font-medium text-ink-700">배송방식</label>
                  <input
                    type="text"
                    value={newRow.deliveryType}
                    onChange={(e) => setNewRow({ ...newRow, deliveryType: e.target.value })}
                    maxLength={20}
                    className="mt-1 w-full rounded border border-ink-200 px-3 py-2.5 text-base focus:outline-none focus:ring-1 focus:ring-coral-500 sm:py-1.5 sm:text-sm"
                  />
                </div>
                <div className="w-24">
                  <label className="block text-sm font-medium text-ink-700">ea(수량)</label>
                  <input
                    type="number"
                    min={1}
                    value={newRow.ea}
                    onChange={(e) => setNewRow({ ...newRow, ea: Number(e.target.value) })}
                    className="mt-1 w-full rounded border border-ink-200 px-3 py-2.5 text-base focus:outline-none focus:ring-1 focus:ring-coral-500 sm:py-1.5 sm:text-sm"
                  />
                </div>
                <div className="w-28">
                  <label className="block text-sm font-medium text-ink-700">배송비</label>
                  <input
                    type="number"
                    min={0}
                    value={newRow.deliveryPrice}
                    onChange={(e) => setNewRow({ ...newRow, deliveryPrice: Number(e.target.value) })}
                    className="mt-1 w-full rounded border border-ink-200 px-3 py-2.5 text-base focus:outline-none focus:ring-1 focus:ring-coral-500 sm:py-1.5 sm:text-sm"
                  />
                </div>
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setAddOpen(false)}
                disabled={adding}
              >
                취소
              </button>
              <button type="button" className="btn-primary" onClick={submitAdd} disabled={adding}>
                {adding ? '추가 중…' : '추가'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="mt-4 rounded-lg border border-coral-200 bg-coral-50/60 px-4 py-3">
        <p className="label-overline text-coral-700">품번코드 안내</p>
        <div className="mt-2 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-ink-700">
          <div className="flex items-center gap-2">
            <span>고체치약 30정</span>
            <span className="rounded bg-paper px-2 py-0.5 font-mono text-[13px] font-medium text-ink-900 ring-1 ring-coral-200">
              1101011
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span>고체치약 200정</span>
            <span className="rounded bg-paper px-2 py-0.5 font-mono text-[13px] font-medium text-ink-900 ring-1 ring-coral-200">
              1101012
            </span>
          </div>
        </div>
      </div>

      <div className="mt-4 overflow-auto rounded-lg border border-ink-200 bg-paper">
        <table className="w-full text-sm">
          <thead className="bg-ink-50 text-ink-600">
            <tr>
              {COLUMNS.map((col) => {
                const narrow = NARROW_FIELDS.has(col.key);
                return (
                  <th
                    key={col.key}
                    className={[
                      'py-2 text-left whitespace-nowrap',
                      narrow ? 'px-2 text-xs w-16' : 'px-3',
                    ].join(' ')}
                  >
                    <span className="inline-flex items-center gap-1">
                      {col.label}
                      {col.editable && (
                        <span
                          className="inline-flex items-center gap-0.5 text-[11px] font-normal text-ink-400"
                          title="수정 가능"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                            className="h-3 w-3"
                            aria-hidden="true"
                          >
                            <path d="M13.586 3.586a2 2 0 1 1 2.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793 3 14.172V17h2.828l8.379-8.379-2.828-2.828z" />
                          </svg>
                        </span>
                      )}
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr>
                <td colSpan={COLUMNS.length} className="px-3 py-8 text-center text-sm text-ink-500">
                  등록된 상품 매칭 코드가 없습니다.
                </td>
              </tr>
            )}
            {items.map((item) => (
              <tr key={item.id} className="border-t border-ink-100 hover:bg-ink-50">
                {COLUMNS.map((col) => {
                  const isEditing = editing?.id === item.id && editing?.field === col.key;
                  const displayVal = item[col.key] != null ? String(item[col.key]) : '—';
                  const narrow = NARROW_FIELDS.has(col.key);
                  return (
                    <td
                      key={col.key}
                      className={[
                        'py-2',
                        narrow ? 'px-2 text-xs w-16' : 'px-3',
                        col.editable ? 'cursor-pointer hover:bg-coral-50' : '',
                        col.key === 'pCodeCh' ? 'font-mono text-xs font-medium text-ink-800' : '',
                      ].join(' ')}
                      onClick={() => col.editable && startEdit(item, col.key)}
                    >
                      {isEditing ? (
                        <input
                          ref={inputRef}
                          className="w-full rounded border border-coral-400 px-1.5 py-0.5 text-sm focus:outline-none focus:ring-1 focus:ring-coral-500"
                          value={draft}
                          onChange={(e) => setDraft(e.target.value)}
                          onBlur={commitEdit}
                          onKeyDown={handleKeyDown}
                          disabled={saving}
                        />
                      ) : (
                        <span className="whitespace-normal break-words">{displayVal}</span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-10">
        <AddedProductCodeSection items={addedItems} />
      </div>
    </div>
  );
}
